import React from 'react';
import { Box, Typography, Paper, Stack } from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import HoverableStockRow from './HoverableStockRow';
import ScrollIndicatorContainer from '../../../components/ScrollIndicatorContainer';
import { HorizontalCardSkeleton } from '../../../components/DashboardSkeletons';

// Helper component for the Logo Avatar
const StockLogo = ({ symbol }) => {
  const shortName = symbol.slice(0, 2).toUpperCase();
  return (
    <Box 
      sx={(theme) => ({ 
        width: 40, 
        height: 40, 
        borderRadius: 1, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        fontWeight: 'bold',
        color: theme.palette.error.main,
        border: `1px solid ${theme.palette.divider}`
      })}
    >
      {shortName}
    </Box>
  );
};

const MostActiveStocksWidget = ({ loading, data }) => {
  if (loading) {
    return (
      <Box sx={{ mb: 0 }}>
        <Typography variant="h6" fontWeight="700" mb={2}>Most Active Stocks</Typography>
        <HorizontalCardSkeleton count={4} />
      </Box>
    );
  }

  const safeData = Array.isArray(data) ? data : [];

  const layoutData = safeData;

  if (layoutData.length === 0) {
    return (
      <Box sx={{ mb: 0 }}>
        <Typography variant="h6" fontWeight="700" mb={2}>Most Active Stocks</Typography>
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid #E0E0E0', borderRadius: 2 }}>
          <Typography color="text.secondary">No active stocks data available currently.</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 0 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight="700">
          Most Active Stocks
        </Typography>
      </Box>

      <ScrollIndicatorContainer>
           {layoutData.map((stock, idx) => {
            // Robust calculation logic mapping LTP and Close Price from multiple possible API properties
            const ltp = parseFloat(stock.ltp || stock.last_price || stock.close || 0);
            const closePrice = parseFloat(stock.close_price || stock.prev_close || stock.previous_close || stock.prevClose || stock.prev_close_price || 0);
            
            const absChange = closePrice === 0 ? 0 : ltp - closePrice;
            const pctChange = closePrice === 0 ? 0 : ((ltp - closePrice) / closePrice) * 100;
            const isPositive = absChange >= 0;

            return (
              <HoverableStockRow key={idx} stock={{
                ...stock,
                token: stock.token,
                exchange: stock.exchange || 'NSE',
                trading_symbol: stock.trading_symbol || stock.symbol,
              }}>
              <Paper 
                elevation={0}
                sx={{ 
                  minWidth: { xs: 180, sm: 200, lg: 220 }, 
                  maxWidth: 240,
                  p: 2, 
                  border: '1px solid #E0E0E0',
                  borderRadius: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  height: 110,
                  overflow: 'hidden',
                  position: 'relative',
                  transition: 'box-shadow 0.2s',
                  '&:hover': {
                     boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                  }
                }}
              >
                {/* Top Row: Logo & Name */}
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <StockLogo symbol={stock.trading_symbol || stock.symbol || 'UNK'} />
                  <Box sx={{ overflow: 'hidden' }}>
                    <Typography variant="subtitle2" fontWeight="700" noWrap>
                      {stock.trading_symbol || stock.symbol}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontSize: '0.65rem' }}>
                      {stock.full_name || stock.companyName || `${stock.trading_symbol || stock.symbol} LIMITED`}
                    </Typography>
                  </Box>
                </Box>
                
                {/* Bottom Row: Price & Change */}
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                  <Typography variant="subtitle2" fontWeight="700" noWrap sx={{ fontSize: { xs: '0.8rem', lg: '0.875rem' } }}>
                    ₹{ltp.toFixed(2)}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                    {isPositive ? (
                      <ArrowDropUpIcon sx={{ color: 'success.main', fontSize: 20 }} />
                    ) : (
                      <ArrowDropDownIcon sx={{ color: 'error.main', fontSize: 20 }} />
                    )}
                    <Typography 
                      variant="caption" 
                      fontWeight="600"
                      noWrap
                      color={isPositive ? 'success.main' : 'error.main'}
                      sx={{ fontSize: { xs: '0.6rem', lg: '0.75rem' } }}
                    >
                      {isPositive ? '+' : ''}₹{Math.abs(absChange).toFixed(2)} ({isPositive ? '+' : ''}{pctChange.toFixed(2)}%)
                    </Typography>
                  </Box>
                </Box>
              </Paper>
              </HoverableStockRow>
            );
          })}
      </ScrollIndicatorContainer>
    </Box>
  );
};

export default MostActiveStocksWidget;
